import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  OnChanges,
  OnInit,
  runInInjectionContext,
  signal,
  SimpleChanges,
  viewChildren,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter, first } from 'rxjs';

import { DayOfWeek } from '@blaze-shared/api-contracts-domain';
import {
  DataBindingSourceInfo,
  findIdColumnKey,
  getActiveBinding,
  isSourceBound,
  makeContainerVisualEffectStyles,
} from '@blaze-shared/elements-domain';
import {
  convertBlazeSchemaToElementSchema,
  createMultipleValuesSchemaWithIsDeletedColumn,
  DatasetRow,
} from '@blaze-shared/schema-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import { ElementLoadingIndicatorComponent, TooltipDirective } from '@blaze-shared/shared-ui';
import { isDeepEqual, useEmitTracker } from '@blaze-shared/shared-util';

import { ElementHeaderComponent } from '../../components/element-header/element-header.component';
import { RowGenerationService } from '../../services/row-generation.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import {
  AvailabilityScheduleConfig,
  AvailabilityScheduleErrors,
  AvailabilityScheduleRule,
  AvailabilityScheduleRuleTimeSlot,
} from './availability.model';
import {
  DataPropsMapping,
  datasetRowsToModel,
  modelToDatasetRows,
  sortDays,
  updateSchedulesStatuses,
  validateSchedule,
} from './availability.utils';
import { AvailabilityScheduleComponent } from './components/availability-schedule/availability-schedule.component';

@UntilDestroy()
@Component({
  selector: 'be-availability',
  templateUrl: './availability.component.html',
  styleUrls: ['./availability.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    AvailabilityScheduleComponent,
    ElementHeaderComponent,
    ElementLoadingIndicatorComponent,
    TooltipDirective,
  ],
  host: {
    '[style.--b-el-internal-padding]': 'internalPadding()',
  },
})
export class AvailabilityComponent
  extends CanvasElementBaseComponent<'availability'>
  implements OnChanges, OnInit
{
  private readonly dialogService = inject(DialogService);
  private readonly elementRef = inject(ElementRef);
  private readonly injector = inject(Injector);
  private readonly rowGenerationService = inject(RowGenerationService);

  protected schedules = viewChildren(AvailabilityScheduleComponent);

  private readonly elementKeyRoot = 'b-avlb';
  private readonly dataValue = this.getDataInput('value');
  private readonly outputEmitTracker = useEmitTracker();

  private propMappings = signal<DataPropsMapping>({} as DataPropsMapping);
  protected elementKey!: string;

  protected isSaving = signal(false);
  protected isSourceBound = signal(false);
  protected labelEnabled = signal(true);
  protected labelText = signal('');
  protected internalPadding = signal<string | undefined>(undefined);

  protected headerStyles = signal<Record<string, string> | null>(null);
  protected wrapperStyles = signal<Record<string, string | undefined> | null>(null);

  protected dataEmptyRow: DatasetRow = {};
  protected dataRows = signal<DatasetRow[]>([]);
  protected defaultTimeSlot = signal<AvailabilityScheduleRuleTimeSlot>({
    from: '09:00:00',
    to: '17:00:00',
    dataRowId: null,
  });
  protected dataSchedules = linkedSignal<
    AvailabilityScheduleConfig[],
    AvailabilityScheduleConfig[]
  >({
    source: () => datasetRowsToModel(this.dataRows(), this.propMappings()),
    computation: (value, prev) => {
      const newSchedules = prev?.value.filter((s) => s.isNew) ?? [];
      return [...value, ...newSchedules];
    },
  });
  protected visibleDays = signal<DayOfWeek[]>([]);
  protected visibleSchedules = computed<AvailabilityScheduleConfig[]>(() => {
    const days = this.visibleDays();
    const schedules = updateSchedulesStatuses(this.dataSchedules());
    const result = schedules.map((schedule) => ({
      ...schedule,
      rules: days.map((day) => {
        const existing: AvailabilityScheduleRule | undefined = schedule.rules.find(
          (r) => r.day === day,
        );
        return (
          existing ?? {
            day,
            enabled: false,
            timeSlots: [],
          }
        );
      }),
    }));
    return result;
  });
  protected schedulesErrors = signal<AvailabilityScheduleErrors[]>([]);
  protected hasMissingConfig = signal<boolean>(true);

  protected hasChanges = computed(() => {
    return this.schedules().some((s) => s.isInitialized() && s.canSave());
  });

  get propsDataSource(): DataBindingSourceInfo | undefined {
    return getActiveBinding(this.props?.data, 'value')?.dataSource;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      const { content } = this.props ?? {};
      const { title, options, input } = content ?? {};
      this.isSourceBound.set(isSourceBound(this.props?.data ?? [], 'value'));
      this.labelEnabled.set(title?.enabled ?? false);
      this.labelText.set(title?.label ?? '');

      const time = options?.time;
      if (time?.from && time.to) {
        this.defaultTimeSlot.set({ from: time.from, to: time.to, dataRowId: null });
      }

      this.hasMissingConfig.set(false);
      this.propMappings.set({
        ...this.propMappings(),
        startDate: this.initPropMapping(input?.startDateColumn),
        endDate: this.initPropMapping(input?.endDateColumn),
        dayOfWeek: this.initPropMapping(input?.dayOfWeekColumn),
        startTime: this.initPropMapping(input?.startTimeColumn),
        endTime: this.initPropMapping(input?.endTimeColumn),
      });
      const currentVisibleDays = this.visibleDays();
      const updatedVisibleDays = sortDays(options?.visibleDays, options?.weekStart) ?? [];
      if (!isDeepEqual(updatedVisibleDays, currentVisibleDays)) {
        this.visibleDays.set(updatedVisibleDays);
        this.updateOutput(false);
      }

      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.dataValue.schema$
      .pipe(filter(Boolean), distinctUntilChanged(isDeepEqual), untilDestroyed(this))
      .subscribe((schema) => {
        const elSchema = createMultipleValuesSchemaWithIsDeletedColumn(
          convertBlazeSchemaToElementSchema(schema, this.elementId, 'change'),
        );
        this.setDefaultEventSchema('change', elSchema);
        const idColumn = this.schemaService.getColumnName(findIdColumnKey(schema));
        if (!idColumn) {
          console.log('Availability: missing id column', { elementId: this.elementId, schema });
          return;
        }
        this.propMappings.set({
          ...this.propMappings(),
          id: idColumn,
        });
      });

    this.dataValue.data$
      .pipe(filter(Boolean), distinctUntilChanged(isDeepEqual), untilDestroyed(this))
      .subscribe((data) => {
        this.dataEmptyRow = this.rowGenerationService.getNewRow(this.propsDataSource);
        this.dataRows.set(data);
        this.isSaving.set(false);
        this.updateOutput(true, data);
      });
  }

  protected onScheduleCopy(index: number): void {
    const current = this.dataSchedules();
    const source = index > -1 ? current[index] : { rules: [] };
    const newSchedule: AvailabilityScheduleConfig = {
      id: crypto.randomUUID(),
      since: null,
      until: null,
      status: 'disabled',
      isNew: true,
      disabledUntil: undefined,
      rules: source.rules.map((r) => ({
        ...r,
        timeSlots: r.timeSlots.map((ts) => ({ ...ts, dataRowId: null })),
      })),
    };
    this.updateSchedules([...current, newSchedule], true);
    // focus the new schedule 'since' date input
    runInInjectionContext(this.injector, () =>
      tryFocusSinceInput(this.elementRef.nativeElement as HTMLElement),
    );
  }

  protected onScheduleDelete(scheduleId: string): void {
    const schedule = this.dataSchedules().find((s) => s.id === scheduleId);
    if (schedule && schedule.isNew) {
      this.updateSchedules(this.dataSchedules().filter((s) => s.id !== scheduleId));
      this.isSaving.set(false);
      return;
    }
    this.dialogService
      .openConfirmDialog({
        title: 'Delete confirmation',
        description: 'Are you sure you want to delete this schedule? It can’t be undone',
        cancelLabel: 'Cancel',
        confirmLabel: 'Yes, delete',
      })
      .afterClosed()
      .pipe(first(), untilDestroyed(this))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.updateSchedules(this.dataSchedules().filter((s) => s.id !== scheduleId));
      });
  }

  protected onScheduleValidate(schedule: AvailabilityScheduleConfig, index: number): void {
    const errors = { ...this.schedulesErrors() };
    const schedules = this.visibleSchedules();

    const { ruleErrors, slotsErrors } = validateSchedule(schedules, schedule);
    errors[index] = { ruleErrors, slotsErrors };
    this.schedulesErrors.set(errors);
  }

  protected onScheduleUpdate(schedule: AvailabilityScheduleConfig): void {
    if (schedule.isNew) {
      delete schedule.isNew;
    }
    this.updateSchedules(this.dataSchedules().map((s) => (s.id !== schedule.id ? s : schedule)));
  }

  private initPropMapping(prop: string | undefined): string {
    const col = this.schemaService.getColumnName(prop);
    if (col) {
      return col;
    }
    this.hasMissingConfig.set(true);
    return `missing:${prop}`;
  }

  private updateSchedules(schedules: AvailabilityScheduleConfig[], skipOutputUpdate = false): void {
    this.dataSchedules.set(schedules);
    if (!skipOutputUpdate) {
      this.updateOutput(false);
    }
  }

  private updateStyles(): void {
    const { style } = this.props ?? {};
    const { general, titleBlock } = style || {};
    if (!general || !titleBlock) {
      return;
    }
    this.internalPadding.set(general.offset.padding);
    this.wrapperStyles.set(makeContainerVisualEffectStyles(general.visualEffect));
    this.headerStyles.set({
      paddingLeft: this.isUsingOldPaddingStrategy ? '16px' : '0',
    });
  }

  private updateOutput(preventConnectionsTrigger: boolean, dataRows?: DatasetRow[]): void {
    const data =
      dataRows?.map((r) => ({ ...r, _isDeleted: r._isDeleted ?? false })) ??
      modelToDatasetRows(
        this.visibleSchedules(),
        this.propMappings(),
        (this.getDataInputValue('value')?.data ?? []) as DatasetRow[],
        this.dataEmptyRow,
      );
    const isInitialData = !this.outputEmitTracker.hasPrevious();
    const schema = this.getEventSchema('change');

    if (!schema || !this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    if (!preventConnectionsTrigger) {
      this.isSaving.set(true);
    }
    this.output.emit({
      eventType: 'change',
      data,
      isInitialData,
      schema,
      preventConnectionsTrigger,
    });
  }
}

function tryFocusSinceInput(el: HTMLElement): void {
  afterNextRender(() => {
    const target = el.querySelector(
      'blaze-availability-schedule:last-of-type .mat-datepicker-input',
    ) as HTMLInputElement;
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', inline: 'center' });
    target.focus();
    setTimeout(() => {
      target.click();
    }, 250);
  });
}
